import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { ServiceFront, WorkOrder, SubWorkOrder } from "../../administration/admin-domain.js";

export default class AdminFrontsPage extends HTMLElement {
  private serviceFronts: ServiceFront[] = [];
  private workOrders: WorkOrder[] = [];
  private subWorkOrders: SubWorkOrder[] = [];
  private clients: any[] = [];
  private searchQuery = "";
  private clientFilter = "ALL";
  private isModalOpen = false;
  private editingFront: ServiceFront | null = null;
  private selectedModalClientId = "";

  public connectedCallback(): void {
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [fronts, wos, subs, cls] = await Promise.all([
        platform.adminService.listServiceFronts(dummyContext),
        platform.adminService.listWorkOrders(dummyContext),
        platform.adminService.listSubWorkOrders(dummyContext),
        platform.adminService.listAuthorizedClients(dummyContext).catch(() => [])
      ]);
      this.serviceFronts = fronts;
      this.workOrders = wos;
      this.subWorkOrders = subs;
      this.clients = cls || [];
      this.render();
    } catch (e) {
      console.error("Failed to load service fronts", e);
    }
  }

  private render(): void {
    const osMap = new Map(this.workOrders.map(wo => [wo.id, wo.code]));
    const subMap = new Map(this.subWorkOrders.map(s => [s.id, s.code]));
    const clientMap = new Map(this.clients.map(c => [c.id, c.name]));

    const filtered = this.serviceFronts.filter(sf => {
      const matchesClient = this.clientFilter === "ALL" || sf.clientId === this.clientFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        sf.name.toLowerCase().includes(term) ||
        (osMap.get((sf.workOrderId ?? '') as any) || '').toLowerCase().includes(term);
      return matchesClient && matchesSearch;
    });

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Frentes de Serviço</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Organização de equipes e locais de trabalho vinculados a Ordens de Serviço por Cliente.</p>
          </div>
          <button id="btn-new-front" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Nova Frente</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center; flex-wrap: wrap;">
          <select id="select-client" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; background: var(--color-surface-primary);">
            <option value="ALL" ${this.clientFilter === "ALL" ? "selected" : ""}>Todos os Clientes</option>
            ${this.clients.map(c => `<option value="${c.id}" ${this.clientFilter === c.id ? "selected" : ""}>${c.name}</option>`).join("")}
          </select>
          <input type="text" id="input-search" placeholder="Buscar por nome da frente ou OS..." value="${this.searchQuery}" style="flex: 1; min-width: 240px; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME DA FRENTE</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CLIENTE</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ORDEM DE SERVIÇO (OS)</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">SUB-OS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="6" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhuma frente de serviço cadastrada.</td>
                </tr>
              ` : filtered.map(sf => `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                  <td style="padding: 12px 16px; font-weight: 600; color: var(--ia-primary);">${sf.name}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 500;">${clientMap.get(sf.clientId) || sf.clientId || "-"}</td>
                  <td style="padding: 12px 16px; color: var(--color-ink); font-family: monospace; font-weight: 600;">${osMap.get((sf.workOrderId ?? '') as any) || sf.workOrderId || '-'}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary);">${sf.subWorkOrderId ? (subMap.get(sf.subWorkOrderId) || sf.subWorkOrderId) : "-"}</td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${sf.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${sf.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${sf.status === 'ACTIVE' ? 'ATIVA' : 'INATIVA'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; text-align: right;">
                    <button class="btn-edit" data-id="${sf.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                    ${sf.status === 'ACTIVE' ? `
                      <button class="btn-inactivate" data-id="${sf.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Inativar</button>
                    ` : ''}
                  </td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderModal(): string {
    const isEdit = !!this.editingFront;
    const currentClientId = this.editingFront ? this.editingFront.clientId : this.selectedModalClientId;
    const availableWos = currentClientId ? this.workOrders.filter(w => w.clientId === currentClientId) : [];
    const selectedWoId = this.editingFront?.workOrderId || "";
    const availableSubs = selectedWoId ? this.subWorkOrders.filter(s => s.workOrderId === selectedWoId) : [];

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 480px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Frente' : 'Nova Frente de Serviço'}</h2>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>

          <div id="front-modal-error" style="display: none; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px;"></div>

          <form id="form-front" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Cliente *</label>
              <select id="modal-client" ${isEdit ? 'disabled' : 'required'} style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${isEdit ? 'var(--color-bg-primary)' : 'white'};">
                <option value="">Selecione o Cliente...</option>
                ${this.clients.map(c => `<option value="${c.id}" ${currentClientId === c.id ? 'selected' : ''}>${c.name}</option>`).join("")}
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Ordem de Serviço (OS) *</label>
              <select id="modal-os" ${!currentClientId ? 'disabled' : 'required'} style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${!currentClientId ? 'var(--color-bg-primary)' : 'white'};">
                <option value="">${!currentClientId ? 'Selecione um cliente primeiro...' : 'Selecione uma OS...'}</option>
                ${availableWos.map(wo => `<option value="${wo.id}" ${this.editingFront?.workOrderId === wo.id ? 'selected' : ''}>${wo.code} - ${wo.description || ''}</option>`).join("")}
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Sub-OS (Opcional)</label>
              <select id="modal-sub-os" ${availableSubs.length === 0 ? 'disabled' : ''} style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${availableSubs.length === 0 ? 'var(--color-bg-primary)' : 'white'};">
                <option value="">Nenhuma Sub-OS</option>
                ${availableSubs.map(s => `<option value="${s.id}" ${this.editingFront?.subWorkOrderId === s.id ? 'selected' : ''}>${s.code} - ${s.description || ''}</option>`).join("")}
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Nome da Frente de Serviço *</label>
              <input type="text" id="modal-name" value="${this.editingFront?.name || ''}" required placeholder="Ex: Frente Caldeiraria, Frente END Ultrassom" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#select-client")?.addEventListener("change", (e) => {
      this.clientFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#btn-new-front")?.addEventListener("click", () => {
      this.editingFront = null;
      this.selectedModalClientId = "";
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingFront = this.serviceFronts.find(sf => sf.id === id) || null;
        this.selectedModalClientId = this.editingFront?.clientId || "";
        this.isModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        if (confirm("Deseja inativar esta frente de serviço?")) {
          const ctx = contextStore.get();
          const dummyContext = {
            activeClientId: ctx.activeClientId,
            permissions: ctx.permissions,
            correlationId: "",
            userId: ctx.userId
          } as any;
          await platform.adminService.inactivateServiceFront(dummyContext, id);
          await this.loadData();
        }
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    // Cascading Client change in Front Modal
    this.querySelector("#modal-client")?.addEventListener("change", (e) => {
      this.selectedModalClientId = (e.target as HTMLSelectElement).value;
      const osSelect = this.querySelector("#modal-os") as HTMLSelectElement;
      const subOsSelect = this.querySelector("#modal-sub-os") as HTMLSelectElement;
      if (osSelect) {
        if (!this.selectedModalClientId) {
          osSelect.disabled = true;
          osSelect.style.background = 'var(--color-bg-primary)';
          osSelect.innerHTML = '<option value="">Selecione um cliente primeiro...</option>';
        } else {
          osSelect.disabled = false;
          osSelect.style.background = 'white';
          const clientWos = this.workOrders.filter(w => w.clientId === this.selectedModalClientId);
          osSelect.innerHTML = '<option value="">Selecione uma OS...</option>' +
            clientWos.map(wo => `<option value="${wo.id}">${wo.code} - ${wo.description || ''}</option>`).join("");
        }
      }
      if (subOsSelect) {
        subOsSelect.disabled = true;
        subOsSelect.style.background = 'var(--color-bg-primary)';
        subOsSelect.innerHTML = '<option value="">Nenhuma Sub-OS</option>';
      }
    });

    // Cascading OS change in Front Modal
    this.querySelector("#modal-os")?.addEventListener("change", (e) => {
      const selectedWoId = (e.target as HTMLSelectElement).value;
      const subOsSelect = this.querySelector("#modal-sub-os") as HTMLSelectElement;
      if (subOsSelect) {
        const availableSubs = selectedWoId ? this.subWorkOrders.filter(s => s.workOrderId === selectedWoId) : [];
        if (availableSubs.length === 0) {
          subOsSelect.disabled = true;
          subOsSelect.style.background = 'var(--color-bg-primary)';
          subOsSelect.innerHTML = '<option value="">Nenhuma Sub-OS disponível</option>';
        } else {
          subOsSelect.disabled = false;
          subOsSelect.style.background = 'white';
          subOsSelect.innerHTML = '<option value="">Nenhuma Sub-OS</option>' +
            availableSubs.map(s => `<option value="${s.id}">${s.code} - ${s.description || ''}</option>`).join("");
        }
      }
    });

    this.querySelector("#form-front")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errorDiv = this.querySelector("#front-modal-error") as HTMLElement;
      if (errorDiv) {
        errorDiv.style.display = "none";
        errorDiv.textContent = "";
      }

      const clientSelect = this.querySelector("#modal-client") as HTMLSelectElement;
      const clientId = this.editingFront ? this.editingFront.clientId : (clientSelect?.value || "");
      if (!clientId) {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = "SELECIONE O CLIENTE.";
        }
        return;
      }

      const workOrderId = (this.querySelector("#modal-os") as HTMLSelectElement).value;
      if (!workOrderId) {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = "SELECIONE A ORDEM DE SERVIÇO (OS).";
        }
        return;
      }

      const subWorkOrderId = (this.querySelector("#modal-sub-os") as HTMLSelectElement).value || null;
      const name = (this.querySelector("#modal-name") as HTMLInputElement).value;

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: clientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (this.editingFront) {
          await platform.adminService.updateServiceFront(dummyContext, {
            ...this.editingFront,
            clientId: clientId as any,
            workOrderId: workOrderId as any,
            subWorkOrderId,
            name
          });
        } else {
          await platform.adminService.createServiceFront(dummyContext, {
            clientId: clientId as any,
            workOrderId,
            subWorkOrderId,
            name
          });
        }
        this.isModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = err.message || "Erro ao salvar frente de serviço.";
        }
      }
    });
  }
}

if (!customElements.get("app-admin-fronts-page")) {
  customElements.define("app-admin-fronts-page", AdminFrontsPage);
}



